import { canGrantRole, type CoachingRole } from "./roles";
import type {
  CoachingArtifactVisibility,
  CoachingDeliveryMode,
  CoachingReportType
} from "../services/coaching-dtos";

export type ArtifactKind = "report" | "transcript" | "resource";
export type AccessState = "allowed" | "redacted" | "generic-not-found" | "disabled-read-only";

export interface ArtifactAccessDecision {
  kind: ArtifactKind;
  role: CoachingRole;
  state: AccessState;
  label: string;
}

export interface ResourceSurface {
  role: CoachingRole;
  canManage: boolean;
  canDownload: boolean;
  label: string;
}

export interface HistoryEvent {
  id: string;
  actorRole: CoachingRole;
  action: string;
  summary: string;
  sensitiveValues: string[];
  actorId?: string;
}

export interface RedactedHistoryEvent {
  id: string;
  actorRole: CoachingRole;
  action: string;
  summary: string;
  renderedSensitiveValues: string[];
}

export interface PermissionGrantDecision {
  granter: CoachingRole;
  target: CoachingRole;
  allowed: boolean;
  label: string;
}

const secretLikePattern = /(secret|token|password|credential|authorization|bearer|api[_-]?key)/i;

export const sensitiveHistoryFixture: HistoryEvent[] = [
  {
    id: "hist-1",
    actorRole: "admin",
    action: "user.updated",
    summary: "Coach assignment changed",
    sensitiveValues: ["coach_id=coach-1", "authorization=Bearer hidden"]
  },
  {
    id: "hist-2",
    actorRole: "company_admin",
    action: "mutation.denied",
    summary: "Company admin attempted read-only session mutation",
    sensitiveValues: ["session_id=session-1", "token=closed"]
  }
];

export function evaluateArtifactAccess(role: CoachingRole, kind: ArtifactKind): ArtifactAccessDecision {
  if (kind === "resource") {
    return {
      role,
      kind,
      state: "allowed",
      label: role === "admin" || role === "super_admin" ? "Resource management available" : "Global read-only resource"
    };
  }

  if (kind === "report") {
    return {
      role,
      kind,
      state: "allowed",
      label: role === "participant" ? "Own report only" : "Role-scoped report access"
    };
  }

  if (role === "participant") {
    return { role, kind, state: "generic-not-found", label: "Transcript unavailable to participants" };
  }

  if (role === "company_admin") {
    return { role, kind, state: "redacted", label: "Transcript redacted for assigned-company view" };
  }

  return { role, kind, state: "allowed", label: "Transcript access allowed within operational scope" };
}

/**
 * D-3 role -> report_type/visibility access matrix (report-generation spec §5.2,
 * org-admin spec §4). This is the FRONTEND belt-and-suspenders of the backend G3
 * read-path predicate: even when a row reaches the UI, a non-staff role must
 * NEVER see vjal_internal / ceo_confidential (or any ceo_out_of_band row).
 *
 * The backend is the source of truth; this evaluator drives the list mapper
 * filter + the per-type badge rendering so a backend regression cannot leak a
 * confidential report into a company_admin / cohort_lead / participant surface.
 */
const reportTypeMatrix: Record<CoachingRole, CoachingReportType[]> = {
  super_admin: ["team_admin", "coach_feedback", "vjal_internal", "ceo_confidential"],
  admin: ["team_admin", "coach_feedback", "vjal_internal", "ceo_confidential"],
  coach: ["team_admin", "coach_feedback"],
  company_admin: ["team_admin"],
  cohort_lead: ["team_admin"],
  // participant reports are participant-owned (own report), not a report_type tier.
  participant: []
};

const visibilityMatrix: Record<CoachingRole, CoachingArtifactVisibility[]> = {
  super_admin: ["staff_only", "staff_and_owner", "company_readable", "participant_readable", "coach_only"],
  admin: ["staff_only", "staff_and_owner", "company_readable", "participant_readable", "coach_only"],
  coach: ["coach_only", "company_readable"],
  company_admin: ["company_readable"],
  cohort_lead: ["company_readable"],
  participant: ["participant_readable"]
};

export function isStaffRole(role: CoachingRole) {
  return role === "super_admin" || role === "admin";
}

export interface ReportTypeAccessInput {
  reportType?: CoachingReportType;
  visibility?: CoachingArtifactVisibility;
  deliveryMode?: CoachingDeliveryMode;
}

/**
 * Row shape the list filter accepts. Visibility/delivery are kept as loose
 * strings here because table-row projections carry them as strings; the
 * matrices treat any unknown value as "not in the allow-list" (deny by
 * default), so a stray value never widens access.
 */
export interface ReportRowAccessFields {
  reportType?: CoachingReportType;
  visibility?: string;
  deliveryMode?: string;
  downloadPolicy?: string;
}

/**
 * Returns whether the role may READ (list/detail) a report of this
 * type/visibility/delivery. Confidential out-of-band rows are staff-only and the
 * download is disabled even when the row itself is staff-visible (acceptance
 * report-gen #20 / org-admin §2.2). Visibility/delivery are matched loosely so
 * string-typed table-row projections work; an unknown value is denied.
 */
export function canRoleReadReport(role: CoachingRole, input: ReportRowAccessFields): boolean {
  // ceo_out_of_band is NEVER an in-dashboard read for a non-staff role.
  if (input.deliveryMode === "ceo_out_of_band" && !isStaffRole(role)) return false;
  if (input.reportType && !reportTypeMatrix[role].includes(input.reportType)) return false;
  if (input.visibility && !(visibilityMatrix[role] as string[]).includes(input.visibility)) return false;
  return true;
}

/**
 * Drops every report row the role may not read per D-3. Staff see everything.
 * For a non-staff role, a `team_admin` (or untyped/legacy) row is scope-governed
 * (a participant's own report, a company_admin's team report, a cohort_lead's
 * assigned-cohort report) and is kept; the leak-risk tiers — `coach_feedback`,
 * `vjal_internal`, `ceo_confidential`, and any `ceo_out_of_band` delivery — are
 * dropped unless the role's report-type matrix explicitly allows them (coach may
 * read coach_feedback). This is the FE half of the §7.4 #15/#18 guarantee.
 */
export function filterReadableReports<T extends ReportRowAccessFields>(role: CoachingRole, rows: T[]): T[] {
  if (isStaffRole(role)) return rows;
  return rows.filter((row) => {
    const reportType = row.reportType ?? "team_admin";
    // team_admin / legacy is scope-governed, not type-gated.
    if (reportType === "team_admin") {
      return row.deliveryMode !== "ceo_out_of_band";
    }
    return canRoleReadReport(role, {
      reportType,
      visibility: row.visibility,
      deliveryMode: row.deliveryMode
    });
  });
}

/** Whether the download control may be enabled for this role/report. */
export function canRoleDownloadReport(role: CoachingRole, input: ReportRowAccessFields): boolean {
  if (input.deliveryMode === "ceo_out_of_band") return false; // out-of-band only, never an in-dashboard download
  if (role === "participant") return false;
  if (role === "company_admin" || role === "cohort_lead") {
    return (
      input.reportType === "team_admin" &&
      input.visibility === "company_readable" &&
      input.downloadPolicy === "company_readable"
    );
  }
  return canRoleReadReport(role, input);
}

export function evaluateResourceSurface(role: CoachingRole): ResourceSurface {
  const canManage = role === "admin" || role === "super_admin";
  return {
    role,
    canManage,
    canDownload: true,
    label: canManage ? "Manage resources and upload readiness" : "Read-only global resources"
  };
}

export function redactHistoryEvent(event: HistoryEvent): RedactedHistoryEvent {
  return {
    id: event.id,
    actorRole: event.actorRole,
    action: event.action,
    summary: event.summary,
    renderedSensitiveValues: event.sensitiveValues.map((value) => (secretLikePattern.test(value) ? "[redacted]" : value))
  };
}

export function historyIsSecretSafe(events: RedactedHistoryEvent[]) {
  return events.every((event) => event.renderedSensitiveValues.every((value) => !secretLikePattern.test(value)));
}

export function evaluatePermissionGrant(granter: CoachingRole, target: CoachingRole): PermissionGrantDecision {
  const allowed = canGrantRole(granter, target);
  return {
    granter,
    target,
    allowed,
    label: allowed ? `${granter} can grant ${target}` : `${granter} cannot grant ${target}`
  };
}

export function getSensitiveSurfaceRows() {
  const roles: CoachingRole[] = ["super_admin", "admin", "coach", "company_admin", "participant"];
  return roles.flatMap((role) => [
    evaluateArtifactAccess(role, "report"),
    evaluateArtifactAccess(role, "transcript"),
    evaluateArtifactAccess(role, "resource")
  ]);
}
